import { z } from "zod";
import {
  CandidateEditableProfileSchema,
  CandidateSessionResponseSchema,
} from "./jobs";

export const CandidateContactSourceSchema = z.enum([
  "jobs_page",
  "talent_network",
]);
export const CandidateEmailLinkRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .email()
    .transform((value) => value.toLowerCase()),
  creatorId: z.string().regex(/^[a-f\d]{24}$/i),
  source: CandidateContactSourceSchema,
});
export type CandidateEmailLinkRequestDto = z.infer<
  typeof CandidateEmailLinkRequestSchema
>;
export const CandidateContactStartSchema = z.object({
  externalId: z.string().uuid(),
  posthogDistinctId: z.string().trim().min(1).max(200),
  source: CandidateContactSourceSchema,
  editableProfile: CandidateEditableProfileSchema.extend({
    email: z.string().trim().email(),
  }).refine(
    (profile) =>
      profile.titles.length > 0 ||
      profile.skills.length > 0 ||
      profile.keywords.length > 0,
    "Add at least one title or skill",
  ),
  sendEmailLink: z.boolean().optional(),
});
export const CandidateContactEmailRequestSchema = z.object({
  externalId: z.string().uuid(),
  source: CandidateContactSourceSchema,
});
export const CandidateContactEmailStatusSchema = z.object({
  emailVerified: z.boolean(),
  emailLinkSent: z.boolean(),
  emailLinkError: z.string().optional(),
});
export const CandidateContactPhoneRequestSchema = z.object({
  externalId: z.string().uuid(),
  source: CandidateContactSourceSchema,
  phone: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/, "Use an E.164 phone number"),
});
export const CandidateContactPhoneVerifySchema =
  CandidateContactPhoneRequestSchema.extend({
    code: z.string().regex(/^\d{4,10}$/, "Enter the verification code"),
  });
export const CandidateEmailLinkVerifySchema = z.object({
  externalId: z.string().uuid(),
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  posthogDistinctId: z.string().trim().min(1).max(200).optional(),
});
export const CandidateContactStatusSchema = CandidateContactEmailStatusSchema.extend({
  authenticated: z.boolean().optional(),
  externalId: z.string().uuid(),
  source: CandidateContactSourceSchema,
  phoneVerified: z.boolean(),
  editableProfile: CandidateEditableProfileSchema,
});
export const CandidateEmailLinkResultSchema = z.object({
  signIn: z.boolean().optional(),
  session: CandidateSessionResponseSchema,
  source: CandidateContactSourceSchema,
  phoneVerified: z.boolean(),
});
export type CandidateContactSource = z.infer<
  typeof CandidateContactSourceSchema
>;
export type CandidateContactStartDto = z.infer<
  typeof CandidateContactStartSchema
>;
export type CandidateContactEmailRequestDto = z.infer<
  typeof CandidateContactEmailRequestSchema
>;
export type CandidateContactEmailStatusDto = z.infer<
  typeof CandidateContactEmailStatusSchema
>;
export type CandidateContactPhoneRequestDto = z.infer<
  typeof CandidateContactPhoneRequestSchema
>;
export type CandidateContactPhoneVerifyDto = z.infer<
  typeof CandidateContactPhoneVerifySchema
>;
export type CandidateEmailLinkVerifyDto = z.infer<
  typeof CandidateEmailLinkVerifySchema
>;
export type CandidateContactStatusDto = z.infer<
  typeof CandidateContactStatusSchema
>;
export type CandidateEmailLinkResultDto = z.infer<
  typeof CandidateEmailLinkResultSchema
>;
